/* eslint-disable unicorn/consistent-function-scoping */
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of, tap } from 'rxjs';
import { PerioExamSiteAndPatientListenerService, ToastService } from '@libs/shared-services';
import { PerioExamRecording, PerioExamRecordingsDataService } from '@ffd/api-client';

@Injectable()
export class PerioExamRecordingsModalService {
  private readonly perioExamRecordingsDataService = inject(PerioExamRecordingsDataService);
  private readonly siteAndPatientListenerService = inject(PerioExamSiteAndPatientListenerService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly siteIdSignal = this.siteAndPatientListenerService.getSiteId();
  private readonly patientIdSignal = this.siteAndPatientListenerService.getPatientId();

  public readonly recordingsSignal = signal<PerioExamRecording[] | null>(null);
  public readonly isLoadingSignal = signal<boolean>(false);
  public readonly errorMessageSignal = signal<string | null>(null);
  public readonly isDeletingSignal = signal<string | null>(null);

  public init(): void {
    this.loadRecordings();
  }

  private loadRecordings(): void {
    const siteId = this.siteIdSignal();
    const patientId = this.patientIdSignal();

    if (!siteId || !patientId) {
      this.errorMessageSignal.set('Unable to load recordings: missing site or patient information');
      return;
    }

    this.isLoadingSignal.set(true);
    this.errorMessageSignal.set(null);

    this.perioExamRecordingsDataService
      .getPerioExamRecordings(siteId, patientId)
      .pipe(
        tap((recordings) => {
          this.recordingsSignal.set(recordings);
          this.isLoadingSignal.set(false);
        }),
        catchError((_error) => {
          this.errorMessageSignal.set('Failed to load recordings. Please try again.');
          this.isLoadingSignal.set(false);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public deleteRecording(recordingId: string): void {
    const siteId = this.siteIdSignal();
    const patientId = this.patientIdSignal();

    if (!siteId || !patientId) {
      this.toastService.error('Unable to delete recording: missing site or patient information');
      return;
    }

    this.isDeletingSignal.set(recordingId);

    this.perioExamRecordingsDataService
      .deletePerioExamRecording(siteId, patientId, recordingId)
      .pipe(
        tap(() => {
          // Remove the deleted recording from the list
          const currentRecordings = this.recordingsSignal();
          if (currentRecordings) {
            this.recordingsSignal.set(currentRecordings.filter((r) => r.recordingId !== recordingId));
          }
          this.isDeletingSignal.set(null);
          this.toastService.success('Recording deleted successfully');
        }),
        catchError((_error) => {
          this.isDeletingSignal.set(null);
          this.toastService.error('Failed to delete recording. Please try again.');
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public isDeleting(recordingId: string): boolean {
    return this.isDeletingSignal() === recordingId;
  }
}
